import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import {
  RadioGroup, RadioGroupItem,
} from "@/components/ui/radio-group";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import {
  Percent, Plus, RefreshCw, Loader2, Infinity as Infty, IndianRupee, Calendar as CalendarIcon,
} from "lucide-react";

/**
 * SuperAdmin — Billing Coupons management.
 *
 * Applies to ALL FOUR checkout surfaces (campaign fee, wallet top-up,
 * static-offer listing duration, featured-slot Boost). Same coupon
 * code shares a single `max_uses` cap across all four surfaces —
 * usage increments only on confirmed payment. Optional expiry uses
 * end-of-day-IST semantics (a coupon dated 31 Aug stays valid
 * through 23:59:59 IST that day) — same helper as campaign expiry.
 */
export default function SuperAdminBillingCouponsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    code: "", discount_type: "percentage", discount_value: "20",
    max_uses: "", note: "", expires_at: "",
    // Phase-21 fields
    starts_at: "",
    per_vendor_limit: "1",
    max_unique_vendors: "",
    min_order_value_rupees: "",
    max_discount_cap_rupees: "",
    is_new_vendor_only: false,
    new_vendor_days: "30",
    signup_grant_rupees: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.adminListBillingCoupons({ page: 1, page_size: 100 });
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load billing coupons.");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onCreate = async (e) => {
    e.preventDefault();
    if (busy) return;
    const payload = {
      code: form.code.trim().toUpperCase(),
      discount_type: form.discount_type,
      discount_value: Number(form.discount_value || 0),
      max_uses: form.max_uses === "" ? null : Number(form.max_uses),
      note: form.note.trim() || null,
      // Emit only when non-blank so backend leaves it null. We send
      // an ISO date-at-midnight-UTC — the backend's core/expiry.py
      // helper then interprets it as end-of-day IST at read time.
      ...(form.expires_at ? { expires_at: `${form.expires_at}T00:00:00Z` } : {}),
      ...(form.starts_at ? { starts_at: `${form.starts_at}T00:00:00Z` } : {}),
      // Phase-21 caps (blank = null in DB = unlimited).
      per_vendor_limit: form.per_vendor_limit === "" ? null : Number(form.per_vendor_limit),
      max_unique_vendors: form.max_unique_vendors === "" ? null : Number(form.max_unique_vendors),
      min_order_value_rupees: form.min_order_value_rupees === "" ? null : Number(form.min_order_value_rupees),
      max_discount_cap_rupees: form.max_discount_cap_rupees === "" ? null : Number(form.max_discount_cap_rupees),
      is_new_vendor_only: !!form.is_new_vendor_only,
      new_vendor_days: Number(form.new_vendor_days || 30),
      signup_grant_rupees: form.signup_grant_rupees === "" ? null : Number(form.signup_grant_rupees),
    };
    if (!payload.code || payload.code.length < 2) { toast.error("Code must be at least 2 characters."); return; }
    // Phase-21: at least one of billing discount or signup grant must be positive.
    const hasBillingDiscount = payload.discount_value > 0;
    const hasSignupGrant = payload.signup_grant_rupees && payload.signup_grant_rupees > 0;
    if (!hasBillingDiscount && !hasSignupGrant) {
      toast.error("Set a discount value OR a signup grant amount (or both). A no-op coupon isn't allowed.");
      return;
    }
    if (payload.discount_type === "percentage" && payload.discount_value > 100) {
      toast.error("Percentage can't exceed 100."); return;
    }
    setBusy(true);
    try {
      await api.saCreateBillingCoupon(payload);
      toast.success(`Created ${payload.code}`);
      setForm({
        code: "", discount_type: "percentage", discount_value: "20", max_uses: "", note: "", expires_at: "",
        starts_at: "", per_vendor_limit: "1", max_unique_vendors: "",
        min_order_value_rupees: "", max_discount_cap_rupees: "",
        is_new_vendor_only: false, new_vendor_days: "30", signup_grant_rupees: "",
      });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create billing coupon.");
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (row) => {
    try {
      await api.saUpdateBillingCoupon(row.id, { is_active: !row.is_active });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update coupon.");
    }
  };

  // Simple inline expiry editor. On save posts PUT with the ISO date,
  // sending `null` when cleared so the backend clears the field.
  const [editingExpiryId, setEditingExpiryId] = useState(null);
  const [expiryDraft, setExpiryDraft] = useState("");
  const openExpiry = (row) => {
    setEditingExpiryId(row.id);
    setExpiryDraft(row.expires_at ? String(row.expires_at).slice(0, 10) : "");
  };
  const saveExpiry = async (row) => {
    try {
      await api.saUpdateBillingCoupon(row.id, {
        expires_at: expiryDraft ? `${expiryDraft}T00:00:00Z` : null,
      });
      toast.success(expiryDraft ? "Expiry updated" : "Expiry cleared");
      setEditingExpiryId(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update expiry.");
    }
  };

  return (
    <div className="space-y-6" data-testid="sa-billing-coupons-page">
      <Toaster richColors position="top-center" />
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Percent className="h-6 w-6" />
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Billing coupons</h1>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Discounts on campaign fees, wallet top-ups, listing durations, and featured-slot Boosts. One code = one shared usage cap across all four.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="sa-coupons-refresh">
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      <Card className="p-5 md:p-6">
        <h2 className="font-bold mb-4 flex items-center gap-2"><Plus className="h-4 w-4" /> Create billing coupon</h2>
        <form onSubmit={onCreate} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Code</Label>
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="SAVE20" className="uppercase tracking-wider" data-testid="sa-coupon-code" />
            </div>
            <div className="space-y-1.5">
              <Label>Note (optional)</Label>
              <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Internal note" data-testid="sa-coupon-note" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div className="space-y-1.5 md:col-span-1">
              <Label>Discount type</Label>
              <RadioGroup value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v })} className="flex gap-4">
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="percentage" id="dt-pct" data-testid="sa-coupon-type-pct" />
                  <Label htmlFor="dt-pct" className="flex items-center gap-1"><Percent className="h-3.5 w-3.5" /> Percentage</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="flat" id="dt-flat" data-testid="sa-coupon-type-flat" />
                  <Label htmlFor="dt-flat" className="flex items-center gap-1"><IndianRupee className="h-3.5 w-3.5" /> Flat</Label>
                </div>
              </RadioGroup>
            </div>
            <div className="space-y-1.5">
              <Label>{form.discount_type === "percentage" ? "Percentage (%)" : "Flat amount (₹)"}</Label>
              <Input type="number" min="0" step="0.01" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })} data-testid="sa-coupon-value" />
            </div>
            <div className="space-y-1.5">
              <Label>Max uses <span className="text-muted-foreground text-xs">(blank = unlimited)</span></Label>
              <Input type="number" min="1" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} placeholder="∞" data-testid="sa-coupon-maxuses" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1">
                <CalendarIcon className="h-3.5 w-3.5" /> Starts on <span className="text-muted-foreground text-xs">(blank = immediately)</span>
              </Label>
              <Input
                type="date"
                value={form.starts_at}
                onChange={(e) => setForm({ ...form, starts_at: e.target.value })}
                data-testid="sa-coupon-starts-at"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1">
                <CalendarIcon className="h-3.5 w-3.5" /> Expiry date <span className="text-muted-foreground text-xs">(blank = no expiry)</span>
              </Label>
              <Input
                type="date"
                value={form.expires_at}
                onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                data-testid="sa-coupon-expires-at"
              />
              <p className="text-[11px] text-muted-foreground">
                Valid through 23:59:59 IST on the selected date.
              </p>
            </div>
          </div>

          {/* Phase-21 caps + limits row */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end pt-2 border-t">
            <div className="space-y-1.5">
              <Label>Per-vendor limit <span className="text-muted-foreground text-xs">(times one vendor can use)</span></Label>
              <Input
                type="number" min="1"
                value={form.per_vendor_limit}
                onChange={(e) => setForm({ ...form, per_vendor_limit: e.target.value })}
                placeholder="1"
                data-testid="sa-coupon-per-vendor-limit"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Max unique vendors <span className="text-muted-foreground text-xs">(blank = unlimited)</span></Label>
              <Input
                type="number" min="1"
                value={form.max_unique_vendors}
                onChange={(e) => setForm({ ...form, max_unique_vendors: e.target.value })}
                placeholder="∞"
                data-testid="sa-coupon-max-unique-vendors"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Min order value (₹) <span className="text-muted-foreground text-xs">(blank = no minimum)</span></Label>
              <Input
                type="number" min="0" step="0.01"
                value={form.min_order_value_rupees}
                onChange={(e) => setForm({ ...form, min_order_value_rupees: e.target.value })}
                placeholder="0"
                data-testid="sa-coupon-min-order"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Max discount cap (₹) <span className="text-muted-foreground text-xs">(useful for % coupons)</span></Label>
              <Input
                type="number" min="0" step="0.01"
                value={form.max_discount_cap_rupees}
                onChange={(e) => setForm({ ...form, max_discount_cap_rupees: e.target.value })}
                placeholder="No cap"
                data-testid="sa-coupon-max-cap"
              />
            </div>
          </div>

          {/* Signup / New Vendor row */}
          <div className="rounded-lg border-2 border-dashed border-black/20 p-4 space-y-3 bg-muted/20">
            <div className="flex items-center gap-3">
              <Switch
                checked={form.is_new_vendor_only}
                onCheckedChange={(v) => setForm({ ...form, is_new_vendor_only: !!v })}
                data-testid="sa-coupon-new-vendor-only"
              />
              <div>
                <Label className="font-semibold">New-vendor-only coupon</Label>
                <p className="text-[11px] text-muted-foreground">
                  Restrict billing use to vendors whose account is under N days old AND has no prior payments.
                </p>
              </div>
            </div>
            {form.is_new_vendor_only && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>New-vendor days threshold</Label>
                  <Input
                    type="number" min="1" max="365"
                    value={form.new_vendor_days}
                    onChange={(e) => setForm({ ...form, new_vendor_days: e.target.value })}
                    data-testid="sa-coupon-new-vendor-days"
                  />
                  <p className="text-[11px] text-muted-foreground">
                    Vendors older than this (or with any prior payment) will be rejected at checkout.
                  </p>
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-black/10">
              <div className="space-y-1.5">
                <Label>Signup grant (₹) <span className="text-muted-foreground text-xs">(blank = not a signup coupon)</span></Label>
                <Input
                  type="number" min="0" step="1"
                  value={form.signup_grant_rupees}
                  onChange={(e) => setForm({ ...form, signup_grant_rupees: e.target.value })}
                  placeholder="e.g. 500"
                  data-testid="sa-coupon-signup-grant"
                />
                <p className="text-[11px] text-muted-foreground">
                  Rupees credited to the vendor's promo wallet when they redeem this at signup.
                  Set this &gt; 0 to enable signup redemption. Set discount value to 0 for a signup-only coupon.
                </p>
              </div>
            </div>
          </div>

          <Button type="submit" disabled={busy} data-testid="sa-coupon-create">
            {busy ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Creating…</> : <><Plus className="h-4 w-4 mr-1.5" /> Create coupon</>}
          </Button>
        </form>
      </Card>

      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-3"><Skeleton className="h-6 w-full" /><Skeleton className="h-6 w-full" /></div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-sm text-muted-foreground" data-testid="sa-coupons-empty">No coupons yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="sa-coupons-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground bg-secondary/40">
                  <th className="py-3 px-4">Code</th>
                  <th className="py-3 px-4">Discount</th>
                  <th className="py-3 px-4">Usage</th>
                  <th className="py-3 px-4">Expires</th>
                  <th className="py-3 px-4">Note</th>
                  <th className="py-3 px-4">Created</th>
                  <th className="py-3 px-4">Active</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => {
                  const expDate = r.expires_at ? new Date(r.expires_at) : null;
                  const now = new Date();
                  // End-of-day-IST tolerance on the FE display only —
                  // authoritative expiry lives in the backend.
                  const expired = expDate && expDate.getTime() + 18.5 * 3600 * 1000 < now.getTime();
                  return (
                    <tr key={r.id} className="border-t border-border" data-testid={`sa-coupon-row-${r.id}`}>
                      <td className="py-3 px-4 font-mono font-semibold">{r.code}</td>
                      <td className="py-3 px-4">
                        {r.discount_type === "percentage" ? `${r.discount_value}%` : `₹${Number(r.discount_value).toFixed(2)}`}
                      </td>
                      <td className="py-3 px-4 tabular-nums">
                        {r.max_uses == null
                          ? <span className="inline-flex items-center gap-1">{r.uses_count} <Infty className="h-3 w-3 opacity-60" /></span>
                          : <>{r.uses_count} / {r.max_uses}</>}
                      </td>
                      <td className="py-3 px-4 text-xs" data-testid={`sa-coupon-expiry-${r.id}`}>
                        {editingExpiryId === r.id ? (
                          <div className="flex items-center gap-1">
                            <Input
                              type="date"
                              value={expiryDraft}
                              onChange={(e) => setExpiryDraft(e.target.value)}
                              className="h-7 text-xs w-[130px]"
                              data-testid={`sa-coupon-expiry-input-${r.id}`}
                            />
                            <Button size="sm" variant="outline" onClick={() => saveExpiry(r)} className="h-7 text-xs" data-testid={`sa-coupon-expiry-save-${r.id}`}>Save</Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditingExpiryId(null)} className="h-7 text-xs">✕</Button>
                          </div>
                        ) : (
                          <button onClick={() => openExpiry(r)} className="text-left" data-testid={`sa-coupon-expiry-open-${r.id}`}>
                            {expDate ? (
                              <span className={expired ? "text-[#8b1212] font-semibold" : "text-foreground"}>
                                {expDate.toLocaleDateString()} {expired ? "(expired)" : ""}
                              </span>
                            ) : (
                              <span className="text-muted-foreground italic">No expiry</span>
                            )}
                          </button>
                        )}
                      </td>
                      <td className="py-3 px-4 text-muted-foreground max-w-[260px] truncate">{r.note || "—"}</td>
                      <td className="py-3 px-4 text-muted-foreground text-xs">{r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}</td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <Switch checked={!!r.is_active} onCheckedChange={() => toggleActive(r)} data-testid={`sa-coupon-toggle-${r.id}`} />
                          <Badge variant="outline" className={r.is_active ? "bg-emerald-100 text-emerald-900 border-emerald-300" : ""}>
                            {r.is_active ? "Active" : "Inactive"}
                          </Badge>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
